import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@clerk/react";
import { useLocation } from "wouter";
import { t } from "@workspace/strings/web";
import { useSignOutDevice, useVerifyDeviceExitPin } from "@workspace/api-client-react";
import { KioskExitPinDialog } from "@/components/player/kiosk-exit-pin-dialog";
import { deviceRequiresExitPin } from "@/player/lib/kiosk-exit-pin";
import { useDevice } from "@/player/context/device-context";
import { readStoredCredential } from "@/player/lib/screen-credential";
import { closePlayerSurface, isPlayerSurface } from "@/lib/player-surface";
import { isDesktopShell } from "@/lib/platform";
import { deviceKioskMode } from "@/lib/device-policy";
import { hasOperatorExited, markOperatorExited } from "@/lib/operator-exit";
import { KIOSK_HOME, shouldReturnToKiosk } from "@/player/lib/kiosk-route";
import { useSignOut } from "@/hooks/use-sign-out";

type ExitAction = "switch-mode" | "sign-out";

interface KioskExitContextValue {
  isKioskPlayer: boolean;
  requestSwitchMode: () => void;
  requestSignOut: () => void;
  /** Remote/admin exit — bypasses kiosk PIN. */
  forceQuit: () => void;
  forceLogout: () => void;
}

const KioskExitContext = createContext<KioskExitContextValue | null>(null);

export function KioskExitProvider({ children }: { children: ReactNode }) {
  const [, navigate] = useLocation();
  const signOut = useSignOut();
  const { credential, device, release } = useDevice();
  const signOutDevice = useSignOutDevice();
  const verifyExitPin = useVerifyDeviceExitPin();

  const [exitPinOpen, setExitPinOpen] = useState(false);
  const [exitPinError, setExitPinError] = useState<string | null>(null);
  const pendingAction = useRef<ExitAction | null>(null);

  // Kiosk trapping + exit-PIN gating apply ONLY when the device is actually in
  // kiosk mode (CB-153). A plain operator player (default) is freely navigable
  // and exits without a PIN — it must never trap a human on /player.
  const isKioskPlayer =
    Boolean(credential ?? readStoredCredential()) &&
    deviceKioskMode(device?.settings);

  const revokeDeviceCredential = useCallback(async () => {
    try {
      await signOutDevice.mutateAsync();
    } catch {
      // Best-effort — always clear locally.
    }
    release();
  }, [signOutDevice, release]);

  // Both exits record the intent BEFORE tearing anything down. Releasing the
  // credential should be enough on its own, but if any step fails — the revoke
  // call, a storage write in private mode — the flag is what stops the route
  // guard from silently pulling the operator straight back into the kiosk.
  const performSwitchMode = useCallback(async () => {
    markOperatorExited();
    // From the operating window, tear down a paired fullscreen player window.
    closePlayerSurface();
    await revokeDeviceCredential();
    // On the desktop PLAYER surface there is no companion window to return to
    // (a resumed kiosk opens the player alone), and this loopback window can't
    // host sign-in. Hand back to the shell: it opens the remote hub + closes
    // this player. Switch-mode keeps the session (signOut=false). On every other
    // surface, navigate within this window.
    if (isDesktopShell() && isPlayerSurface() && window.omnimediaDesktop?.exitToHub) {
      void window.omnimediaDesktop.exitToHub(false);
      return;
    }
    navigate("/start");
  }, [revokeDeviceCredential, navigate]);

  const performSignOut = useCallback(async () => {
    markOperatorExited();
    closePlayerSurface();
    await revokeDeviceCredential();
    // Sign-out from the desktop player surface: hand to the shell with
    // signOut=true so it also clears the hub's Clerk cookies (the loopback
    // player can't clear the remote hub's first-party session itself).
    if (isDesktopShell() && isPlayerSurface() && window.omnimediaDesktop?.exitToHub) {
      void window.omnimediaDesktop.exitToHub(true);
      return;
    }
    // The credential is already revoked above, so no beforeSignOut here; this
    // call is what clears branding, the query cache and the workspace id.
    await signOut();
  }, [revokeDeviceCredential, signOut]);

  const runPendingAction = useCallback(async () => {
    const action = pendingAction.current;
    pendingAction.current = null;
    if (action === "switch-mode") await performSwitchMode();
    else if (action === "sign-out") await performSignOut();
  }, [performSwitchMode, performSignOut]);

  const requestProtectedExit = useCallback(
    (action: ExitAction) => {
      if (!isKioskPlayer) {
        if (action === "switch-mode") void performSwitchMode();
        else void performSignOut();
        return;
      }
      if (!deviceRequiresExitPin(device?.settings)) {
        if (action === "switch-mode") void performSwitchMode();
        else void performSignOut();
        return;
      }
      pendingAction.current = action;
      setExitPinError(null);
      setExitPinOpen(true);
    },
    [device?.settings, isKioskPlayer, performSwitchMode, performSignOut],
  );

  const handleExitPinConfirm = useCallback(
    async (pin: string) => {
      setExitPinError(null);
      try {
        const result = await verifyExitPin.mutateAsync({ data: { pin } });
        if (!result.valid) {
          setExitPinError(t("player.exit.incorrectPin"));
          return;
        }
        setExitPinOpen(false);
        await runPendingAction();
      } catch {
        setExitPinError(t("player.exit.verifyFailed"));
      }
    },
    [verifyExitPin, runPendingAction],
  );

  const value = useMemo<KioskExitContextValue>(
    () => ({
      isKioskPlayer,
      requestSwitchMode: () => requestProtectedExit("switch-mode"),
      requestSignOut: () => requestProtectedExit("sign-out"),
      forceQuit: () => void performSwitchMode(),
      forceLogout: () => void performSignOut(),
    }),
    [isKioskPlayer, requestProtectedExit, performSwitchMode, performSignOut],
  );

  return (
    <KioskExitContext.Provider value={value}>
      {children}
      <KioskExitPinDialog
        open={exitPinOpen}
        title={t("player.exit.dialogTitle")}
        description={t("player.exit.dialogDescription")}
        pending={verifyExitPin.isPending}
        error={exitPinError}
        onConfirm={handleExitPinConfirm}
        onCancel={() => {
          if (verifyExitPin.isPending) return;
          setExitPinOpen(false);
          pendingAction.current = null;
          setExitPinError(null);
        }}
      />
    </KioskExitContext.Provider>
  );
}

export function useKioskExit(): KioskExitContextValue {
  const ctx = useContext(KioskExitContext);
  if (!ctx) {
    return {
      isKioskPlayer: false,
      requestSwitchMode: () => undefined,
      requestSignOut: () => undefined,
      forceQuit: () => undefined,
      forceLogout: () => undefined,
    };
  }
  return ctx;
}

/**
 * Keep an unattended kiosk on `/player`, without ever trapping a human there.
 * The rule (and why the auth routes are exempt) lives in
 * `@/player/lib/kiosk-route`; releasing the device is still PIN-guarded.
 */
export function KioskRouteGuard() {
  const { isLoaded, isSignedIn } = useAuth();
  const [location, navigate] = useLocation();
  const { isKioskPlayer } = useKioskExit();

  if (!isLoaded) return null;

  if (
    shouldReturnToKiosk({
      isKioskPlayer,
      isSignedIn: Boolean(isSignedIn),
      operatorExited: hasOperatorExited(),
      location,
    })
  ) {
    navigate(KIOSK_HOME, { replace: true });
  }

  return null;
}
